/**
 * Client for `/api/media/composite-overlays`.
 *
 * The download button used to flatten overlays in the browser with a Canvas 2D
 * pass (`composite-overlays.ts`) while the publish path flattened nothing at
 * all. Fixing publish by adding a SECOND renderer would have swapped one bug
 * for a subtler one: two implementations of the same layer schema, drifting
 * apart, with no test that could catch it — the download would look right and
 * the published image would look slightly wrong.
 *
 * So download now asks the same server route the publish dispatcher asks. One
 * renderer, one output, no drift by construction.
 */
import { supabase } from '@/integrations/supabase/client';
import type { OverlayLayer } from '@/lib/media-studio/overlay-types';

export interface CompositedMedia {
  sourceUrl: string;
  url: string;
  composited: boolean;
}

/**
 * Resolve an image URL to its flattened equivalent.
 *
 * Rejects when the overlay cannot be reproduced — the caller decides what to
 * do. (For a download, falling back to the raw image is acceptable and honest,
 * because the user is looking at the file they receive. For a publish it is
 * not, which is why the dispatcher treats the same failure as fatal.)
 *
 * `layers`, when given, is sent as an inline override for `imageUrl` — the
 * ad workbench's case, where the layers live client-side (per-ratio, on the
 * concept's variant-set document) rather than on a `media.overlay_layers`
 * row the route could look up itself. Omit it for the plain "flatten
 * whatever this URL's own `media` row carries" case (`ImageBody`, publish).
 */
export async function requestCompositedUrl(
  imageUrl: string,
  layers?: OverlayLayer[],
): Promise<CompositedMedia> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in');

  const response = await fetch('/api/media/composite-overlays', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      imageUrls: [imageUrl],
      ...(layers && layers.length > 0 ? { layerOverrides: { [imageUrl]: layers } } : {}),
    }),
  });

  const payload = (await response.json().catch(() => null)) as {
    results?: CompositedMedia[];
    error?: string;
  } | null;

  if (!response.ok) {
    throw new Error(payload?.error ?? `Compositing failed (HTTP ${response.status})`);
  }
  const result = payload?.results?.[0];
  if (!result) throw new Error('Compositing returned no result');
  return result;
}
